import React, { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

const GNSSSettlementPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState('gnss-settlement');

  const menuItems = [
    { id: 'overview', label: '통합 자동화 계측', href: '/overview' },
    { id: 'smart-pbd', label: '스마트 P.B.D. 통합관리', href: '/smart-pbd' },
    { id: 'smart-dcm', label: '스마트 DCM 통합관리', href: '/smart-dcm' },
    { id: 'gnss-settlement', label: '지반침하 GNSS 무선 침하계', href: '/gnss-settlement' }
  ];

  const benefits = [
    {
      icon: 'ri-satellite-line',
      title: 'GNSS 정밀 측위',
      description: '위성 기반 고정밀 지반침하 측정 및 모니터링'
    },
    {
      icon: 'ri-wifi-line',
      title: '무선 데이터 전송',
      description: '실시간 무선 통신을 통한 즉시 데이터 수집'
    },
    {
      icon: 'ri-alert-line',
      title: '조기 경보 시스템',
      description: '위험 수준 도달 시 즉시 알림 및 대응'
    },
    {
      icon: 'ri-dashboard-line',
      title: '통합 관제',
      description: '다중 지점 동시 모니터링 및 통합 관리'
    }
  ];

  const systemFeatures = [
    {
      number: '01',
      title: '광역·실시간 침하 모니터링',
      description: '배후단지 전역을 다점에서 상시 계측하고 자동 수집',
      highlight: '기존 연 1~2회 인력 측량 방식의 공백과 누락 해소',
      icon: 'ri-map-2-line',
      gradient: 'from-blue-500 to-blue-700',
      image: 'https://public.readdy.ai/ai/img_res/6a4c770dc8120a3d6fe04b7054eec451.jpg'
    },
    {
      number: '02',
      title: '±1 mm급 고정밀 GNSS 무선 침하계',
      description: 'Kalman Filter, SigmaTrim 등 보정 알고리즘으로 오차 약 0.8~1 mm 수준 확보',
      highlight: '지반 계측 시방서 허용오차(±1 mm) 충족',
      icon: 'ri-focus-3-line',
      gradient: 'from-orange-500 to-orange-700',
      image: 'https://readdy.ai/api/search-image?query=High%20precision%20GNSS%20settlement%20gauge%20with%20millimeter%20accuracy%2C%20advanced%20positioning%20equipment%2C%20Kalman%20filter%20algorithm%20visualization%2C%20orange%20and%20white%20color%20scheme%2C%20professional%20measurement%20technology&width=600&height=400&seq=gnss-precision&orientation=landscape'
    },
    {
      number: '03',
      title: '완전 무선·자가발전 구조',
      description: '태양광+배터리로 상시 운영, LoRa/LTE 통신으로 배선·전원 공사 최소화',
      highlight: '항만·준설매립지와 같은 열악한 환경에서도 안정적 장기 운영',
      icon: 'ri-sun-line',
      gradient: 'from-green-500 to-green-700',
      image: 'https://readdy.ai/api/search-image?query=Solar%20powered%20wireless%20GNSS%20settlement%20monitoring%20station%2C%20self-sustaining%20monitoring%20equipment%20with%20solar%20panels%20and%20battery%2C%20LoRa%20wireless%20communication%2C%20orange%20and%20white%20color%20scheme%2C%20autonomous%20monitoring%20system&width=600&height=400&seq=gnss-wireless-power&orientation=landscape'
    },
    {
      number: '04',
      title: '무인·비접근 구역 계측 가능',
      description: '위험 지역·광범위 부지를 인력 투입 없이 원격 계측',
      highlight: '안전성 향상과 현장 인력 운영 부담 감소',
      icon: 'ri-shield-check-line',
      gradient: 'from-red-500 to-red-700',
      image: 'https://readdy.ai/api/search-image?query=Remote%20unmanned%20settlement%20monitoring%20in%20hazardous%20area%2C%20automated%20GNSS%20monitoring%20without%20human%20access%2C%20dangerous%20zone%20monitoring%2C%20orange%20and%20white%20color%20scheme%2C%20safety%20monitoring%20technology&width=600&height=400&seq=gnss-unmanned&orientation=landscape'
    },
    {
      number: '05',
      title: '자동 분석·평가·보고서 출력',
      description: '쌍곡선법·평방근법·아사오카법 등 내장 분석 툴로 잔류침하 및 안정성 자동 계산',
      highlight: '침하안정관리 업무를 시스템화·표준화',
      icon: 'ri-file-chart-line',
      gradient: 'from-purple-500 to-purple-700',
      image: 'https://readdy.ai/api/search-image?query=Automated%20settlement%20analysis%20report%20generation%2C%20hyperbolic%20method%20and%20Asaoka%20method%20analysis%20graphs%2C%20settlement%20stability%20evaluation%20dashboard%2C%20orange%20and%20white%20color%20scheme%2C%20professional%20analysis%20software&width=600&height=400&seq=gnss-analysis&orientation=landscape'
    },
    {
      number: '06',
      title: '통합 플랫폼 연동성',
      description: 'MQTT, GeoJson 기반 표준 인터페이스로 상위 항만·인프라 관제시스템과 통합',
      highlight: '스마트 항만·스마트 인프라 플랫폼과 자연스럽게 연계',
      icon: 'ri-links-line',
      gradient: 'from-indigo-500 to-indigo-700',
      image: 'https://readdy.ai/api/search-image?query=Integrated%20platform%20connectivity%20for%20smart%20port%20infrastructure%2C%20MQTT%20and%20GeoJson%20interface%2C%20system%20integration%20dashboard%2C%20orange%20and%20white%20color%20scheme%2C%20smart%20infrastructure%20platform&width=600&height=400&seq=gnss-integration&orientation=landscape'
    },
    {
      number: '07',
      title: '검증된 신뢰성과 경제성',
      description: '공인시험기관(KTL 등) 성능시험 및 실제 현장(새만금, 여수 산업단지 등) 적용으로 검증',
      highlight: '장기(약 8.5년 이후) 운용 시 기존 인력 계측 대비 누적 비용 절감 효과 뚜렷',
      icon: 'ri-medal-line',
      gradient: 'from-yellow-500 to-yellow-700',
      image: 'https://readdy.ai/api/search-image?query=Verified%20GNSS%20settlement%20monitoring%20system%20with%20certification%2C%20cost-effective%20long-term%20monitoring%20solution%2C%20industrial%20site%20application%2C%20orange%20and%20white%20color%20scheme%2C%20proven%20monitoring%20technology&width=600&height=400&seq=gnss-verified&orientation=landscape'
    },
    {
      number: '08',
      title: '사용자 친화 운영환경',
      description: '웹/모바일 기반 UI로 실시간 조회, 경보 확인, 현장 입력 지원',
      highlight: '발주처·시공사·계측사가 동시에 같은 데이터를 공유하며 선제 대응 가능',
      icon: 'ri-smartphone-line',
      gradient: 'from-pink-500 to-pink-700',
      image: 'https://readdy.ai/api/search-image?query=User-friendly%20web%20and%20mobile%20interface%20for%20settlement%20monitoring%2C%20real-time%20data%20dashboard%20on%20multiple%20devices%2C%20collaborative%20monitoring%20platform%2C%20orange%20and%20white%20color%20scheme%2C%20modern%20monitoring%20UI&width=600&height=400&seq=gnss-user-interface&orientation=landscape'
    }
  ];

  const technologies = [
    {
      name: 'GNSS 정밀 측위 시스템',
      description: '위성 기반 고정밀 지반침하 측정 기술',
      image: 'https://readdy.ai/api/search-image?query=GNSS%20precision%20positioning%20system%20for%20ground%20settlement%20monitoring%2C%20satellite%20positioning%20equipment%2C%20GPS%20monitoring%20station%2C%20orange%20and%20white%20color%20scheme%2C%20professional%20surveying%20technology&width=400&height=300&seq=gnss-positioning&orientation=landscape'
    },
    {
      name: '무선 통신 네트워크',
      description: '실시간 데이터 전송을 위한 무선 통신 시스템',
      image: 'https://readdy.ai/api/search-image?query=Wireless%20communication%20network%20for%20settlement%20monitoring%2C%20wireless%20data%20transmission%20equipment%2C%20communication%20towers%20and%20antennas%2C%20orange%20and%20white%20color%20scheme%2C%20modern%20wireless%20technology&width=400&height=300&seq=gnss-wireless&orientation=landscape'
    },
    {
      name: '데이터 분석 플랫폼',
      description: '지반침하 데이터 분석 및 예측 시스템',
      image: 'https://readdy.ai/api/search-image?query=Ground%20settlement%20data%20analysis%20platform%2C%20settlement%20monitoring%20dashboard%2C%20geological%20data%20visualization%2C%20orange%20and%20white%20color%20scheme%2C%20professional%20monitoring%20software&width=400&height=300&seq=gnss-analytics&orientation=landscape'
    },
    {
      name: '조기 경보 시스템',
      description: '위험 상황 감지 및 즉시 알림 시스템',
      image: 'https://readdy.ai/api/search-image?query=Early%20warning%20system%20for%20ground%20settlement%2C%20alert%20notification%20interface%2C%20emergency%20monitoring%20system%2C%20orange%20and%20white%20color%20scheme%2C%20safety%20monitoring%20technology&width=400&height=300&seq=gnss-warning&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <div className="flex">
        {/* 왼쪽 사이드 메뉴바 */}
        <aside className="w-80 bg-white shadow-lg border-r border-gray-200 min-h-screen sticky top-16">
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6">Product</h2>
            <nav className="space-y-2">
              {menuItems.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  className={`block px-4 py-3 rounded-lg transition-colors cursor-pointer ${
                    activeSection === item.id
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                  }`}
                  onClick={() => setActiveSection(item.id)}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* 메인 콘텐츠 */}
        <main className="flex-1">
          {/* Hero Section */}
          <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-800 text-white">
            <div className="container mx-auto px-6">
              <div className="max-w-4xl mx-auto text-center">
                <h1 className="text-5xl font-bold mb-6">
                  지반침하 GNSS 무선 침하계
                </h1>
                <p className="text-xl mb-8 leading-relaxed">
                  GNSS 위성 기술과 무선 통신을 결합하여 지반침하를 실시간으로 
                  정밀 모니터링하고 조기 경보를 제공하는 첨단 지반 안전 관리 시스템입니다.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-12">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                      <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <i className={`${benefit.icon} text-2xl`}></i>
                      </div>
                      <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                      <p className="text-sm opacity-90">{benefit.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* 시스템 특징 */}
          <section className="py-20 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">
                  시스템 특징
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  지반침하 GNSS 무선 침하계의 8가지 핵심 특징으로 시공 품질과 효율성을 혁신적으로 향상시킵니다
                </p>
              </div>

              <div className="space-y-16">
                {systemFeatures.map((feature, index) => (
                  <div 
                    key={index} 
                    className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                      index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                    }`}
                  >
                    <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                      <div className={`bg-gradient-to-r ${feature.gradient} text-white rounded-2xl p-8 shadow-xl`}>
                        <div className="flex items-start mb-6">
                          <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mr-4">
                            <i className={`${feature.icon} text-3xl`}></i>
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-bold opacity-90 mb-2">{feature.number}</div>
                            <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                          </div>
                        </div>
                        <p className="text-lg mb-4 leading-relaxed opacity-95">
                          {feature.description}
                        </p>
                        <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border-l-4 border-white">
                          <div className="flex items-start">
                            <i className="ri-check-line text-xl mr-2 mt-1"></i>
                            <p className="font-semibold">{feature.highlight}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                      <div 
                        className="h-96 bg-cover bg-center rounded-2xl shadow-xl"
                        style={{ backgroundImage: `url('${feature.image}')` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 핵심 기술 */}
          <section className="py-20 bg-gray-50">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">
                  핵심 기술
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  지반침하 모니터링을 위한 첨단 GNSS 기술 스택
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {technologies.map((tech, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                    <div 
                      className="h-48 bg-cover bg-center"
                      style={{ backgroundImage: `url(${tech.image})` }}
                    ></div>
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">
                        {tech.name}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {tech.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-orange-600 text-white">
            <div className="container mx-auto px-6 text-center">
              <h2 className="text-4xl font-bold mb-6">
                지반침하 GNSS 무선 침하계를 경험해보세요
              </h2>
              <p className="text-xl mb-8 max-w-3xl mx-auto">
                지반 안전을 위한 최첨단 GNSS 모니터링 시스템으로 정확하고 신뢰할 수 있는 지반침하 관리를 시작하세요.
              </p>
              <div className="flex justify-center">
                <a 
                  href="#contact" 
                  className="bg-white text-orange-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  무료 상담 신청
                </a>
              </div>
            </div>
          </section>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default GNSSSettlementPage;
